'use client';

import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  InfoCard,
  Input,
  Select,
  Tooltip,
  TooltipTrigger,
  QuestionMark,
  type SelectItemType
} from '@goodneighbor/ui';
import styles from './partnerSignUpForm.module.scss';
import { usePartnerSignUp, type RegistrationStep } from './usePartnerSignUp';
import OtpForm from '@/app/partner/components/OtpForm';
import {
  ORGANIZATION_TYPES,
  PARTICIPATION_INTERESTS,
  LANGUAGE_PREFERENCES,
  REFERRAL_SOURCES
} from '@goodneighbor/api/routes/organizations/registration/registration.schema';
import usePartnerSlug from './usePartnerSlug';

export { type RegistrationStep } from './usePartnerSignUp';

const organizationTypeItems: SelectItemType[] = ORGANIZATION_TYPES.map(
  (type) => ({
    id: type,
    label: type
  })
);

const participationInterestItems: SelectItemType[] =
  PARTICIPATION_INTERESTS.map((interest) => ({
    id: interest,
    label: interest
  }));

const languagePreferenceItems: SelectItemType[] = LANGUAGE_PREFERENCES.map(
  (lang) => ({
    id: lang,
    label: lang
  })
);

const referralSourceItems: SelectItemType[] = REFERRAL_SOURCES.map(
  (source) => ({
    id: source,
    label: source
  })
);

const yesNoItems: SelectItemType[] = [
  { id: 'Yes', label: 'Yes' },
  { id: 'No', label: 'No' }
];

type PartnerSignUpFormProps = {
  onStepChange?: (step: RegistrationStep) => void;
};

export default function PartnerSignUpForm({
  onStepChange
}: PartnerSignUpFormProps) {
  const {
    form,
    step,
    pendingValues,
    otpValue,
    otpLength,
    otpVerificationError,
    formErrorMessage,
    isSendOtpPending,
    isVerifyOtpPending,
    onSubmit,
    handleOtpChange,
    handleVerificationSubmit,
    handleResend
  } = usePartnerSignUp({ onStepChange });

  const {
    slug: _slug,
    setSlug,
    isChecking: isCheckingSlug,
    isAvailable: _isSlugAvailable,
    isTaken: isSlugTaken,
    slugStatusInfo,
    handleOrganizationNameChange
  } = usePartnerSlug({ form });

  if (step === 'success') {
    return (
      <section
        className={`${styles.verificationContainer} ${styles.successContainer}`}
      >
        <div className={styles.successIconWrapper} aria-hidden>
          <img
            src="/placeholder/celebration.svg"
            alt=""
            className={styles.successIcon}
            aria-hidden
          />
        </div>
        <h2 className={styles.successTitle}>Verification complete</h2>
        <p className={styles.verificationSubtitle}>
          Thank you! We have received your request. We will be in touch with you
          shortly.
        </p>
      </section>
    );
  }

  if (step === 'verify' && pendingValues) {
    return (
      <OtpForm
        onSubmit={handleVerificationSubmit}
        email={pendingValues.email}
        otpValue={otpValue}
        handleOtpChange={handleOtpChange}
        otpLength={otpLength}
        verificationError={otpVerificationError}
        isVerifyOtpPending={isVerifyOtpPending}
        handleResend={handleResend}
        isSendOtpPending={isSendOtpPending}
      />
    );
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form}>
          {/* Section 1: Tell us about your organization */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                Tell us about your organization
              </h2>
              <span className={styles.sectionStep}>Step 1 of 3</span>
            </div>

            <div className={styles.sectionFields}>
              <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        onChange={(value) =>
                          handleOrganizationNameChange(value, field.onChange)
                        }
                        id="organizationName"
                        label="Organization name"
                        placeholder="Organization name"
                        hideIcon
                        labelClassName={styles.label}
                        wrapperClassName={styles.inputWrapper}
                        inputClassName={styles.inputField}
                        isRequired
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className={styles.verificationGroup}>
                <p className={styles.label}>
                  Organization verification
                  <span className={styles.requiredIndicator}>*</span>
                </p>
                <p className={styles.helperText}>
                  Please provide a website or EIN for verification.
                </p>
              </div>

              <div className={styles.fieldRow}>
                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="websiteUrl"
                          type="text"
                          label="Website"
                          placeholder="example.com"
                          prefix="https://"
                          prefixVariant="segmented"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="einNumber"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="einNumber"
                          label="Organization EIN"
                          placeholder="123456789"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className={styles.fieldRow}>
                <FormField
                  control={form.control}
                  name="organizationType"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <Select
                        id="organizationType"
                        label="Organization type"
                        labelClassName={styles.label}
                        placeholder="Business or Brand"
                        items={organizationTypeItems}
                        selectedKey={field.value}
                        onSelectionChange={(key) => field.onChange(key)}
                      >
                        {(item) => (
                          <Select.Item id={item.id}>{item.label}</Select.Item>
                        )}
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="streetAddress"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="streetAddress"
                          label="Organization Street Address"
                          placeholder="123 Main Street"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                          isRequired
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Landing Page URL Field */}
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <FormControl>
                      <div className={styles.slugFieldContainer}>
                        <label htmlFor="slug" className={styles.label}>
                          Landing page URL{' '}
                          <span className={styles.required}>*</span>
                        </label>
                        <p className={styles.slugDescription}>
                          This will be your display URL to all users
                        </p>
                        <div className={styles.slugInputWrapper}>
                          <span className={styles.slugPrefix}>
                            {process.env
                              .NEXT_PUBLIC_ORGANIZATION_PUBLIC_ENDPOINT || ''}
                          </span>
                          <input
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              setSlug(e.target.value);
                            }}
                            id="slug"
                            type="text"
                            placeholder="organization-name"
                            className={styles.slugInput}
                            aria-describedby="slug-status"
                          />
                          <Tooltip
                            title="Organization URLs cannot be changed after creation."
                            placement="top"
                          >
                            <TooltipTrigger
                              className={`${styles.slugInputTooltip} cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover`}
                            >
                              <QuestionMark className="size-4" />
                            </TooltipTrigger>
                          </Tooltip>
                        </div>
                        {slugStatusInfo && (
                          <p
                            id="slug-status"
                            className={`${styles.slugStatus} ${styles[`slugStatus--${slugStatusInfo.type}`]}`}
                          >
                            {slugStatusInfo.message}
                          </p>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Section 2: Organization Point of Contact */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                Organization Point of Contact
              </h2>
              <span className={styles.sectionStep}>Step 2 of 3</span>
            </div>

            <div className={styles.sectionFields}>
              <div className={styles.fieldRow}>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="firstName"
                          label="First Name"
                          placeholder="Maya"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                          isRequired
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="lastName"
                          label="Last Name"
                          placeholder="Smith"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                          isRequired
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className={styles.fieldRow}>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="phone"
                          type="tel"
                          label="Phone Number"
                          placeholder="(555) 555-5555"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.phoneInputWrapper}
                          inputClassName={styles.inputField}
                          isRequired
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="email"
                          type="email"
                          label="Email"
                          placeholder="Email"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                          isRequired
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Section 3: Additional Information */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Additional information</h2>
              <span className={styles.sectionStep}>Step 3 of 3</span>
            </div>

            <div className={styles.sectionFields}>
              <FormField
                control={form.control}
                name="referralSource"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <Select
                      id="referralSource"
                      label="How did you hear about us?"
                      labelClassName={styles.label}
                      placeholder="Select one..."
                      items={referralSourceItems}
                      selectedKey={field.value}
                      onSelectionChange={(key) => field.onChange(key)}
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('referralSource') === 'Other (please specify)' && (
                <FormField
                  control={form.control}
                  name="referralSourceOther"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="referralSourceOther"
                          label="Please specify how you heard about us"
                          placeholder="Describe how you heard about us"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="networkDenominationAssociation"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <Select
                      id="networkDenominationAssociation"
                      label="Are you associated with a participating network or denomination?"
                      labelClassName={styles.label}
                      placeholder="Select one..."
                      items={yesNoItems}
                      selectedKey={field.value}
                      onSelectionChange={(key) => {
                        field.onChange(key);
                        if (key === 'No') {
                          form.setValue('networkDenomination', 'No');
                        } else {
                          form.setValue('networkDenomination', '');
                        }
                      }}
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('networkDenominationAssociation') === 'Yes' && (
                <FormField
                  control={form.control}
                  name="networkDenomination"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="networkDenomination"
                          label="Network or denomination name"
                          placeholder="Network/Denomination Name"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                          isRequired
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="languagePreference"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <Select
                      id="languagePreference"
                      label="Do you identify as a predominantly English, Spanish, or Bilingual speaking organization?"
                      labelClassName={styles.label}
                      placeholder="Select one..."
                      items={languagePreferenceItems}
                      selectedKey={field.value}
                      onSelectionChange={(key) => field.onChange(key)}
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('languagePreference') ===
                'Other (please specify)' && (
                <FormField
                  control={form.control}
                  name="languagePreferenceOther"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="languagePreferenceOther"
                          label="Please specify your language preference"
                          placeholder="Describe your language preference"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="participationInterests"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <label className={styles.label}>
                      In what specific ways does your organization hope to
                      participate in Good Neighbor Day America?
                    </label>
                    <div className={styles.checkboxGroup}>
                      {participationInterestItems.map((item) => (
                        <Checkbox
                          key={item.id}
                          isSelected={field.value?.includes(item.id)}
                          onChange={(isSelected) => {
                            const current = field.value || [];
                            if (isSelected) {
                              field.onChange([...current, item.id]);
                            } else {
                              field.onChange(
                                current.filter((id) => id !== item.id)
                              );
                            }
                          }}
                          label={item.label}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form
                .watch('participationInterests')
                ?.includes(
                  'Provide goods and/or services through other Serve Events Regionally or Nationally'
                ) && (
                <FormField
                  control={form.control}
                  name="participationInterestGoodsServices"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value)}
                          id="participationInterestGoodsServices"
                          label="Please provide a short description of the Goods/Services you can provide and the quantity you can scale"
                          placeholder="Describe the goods/services and quantity"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="hasMultipleLocations"
                render={({ field }) => (
                  <FormItem className={styles.formItem}>
                    <Select
                      id="hasMultipleLocations"
                      label="Does your organization have multiple locations that will host Serve Events on May 16?"
                      labelClassName={styles.label}
                      placeholder="Select one..."
                      items={yesNoItems}
                      selectedKey={field.value}
                      onSelectionChange={(key) => {
                        field.onChange(key);
                        if (key === 'No') {
                          form.setValue('numberOfLocations', undefined);
                        }
                      }}
                    >
                      {(item) => (
                        <Select.Item id={item.id}>{item.label}</Select.Item>
                      )}
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('hasMultipleLocations') === 'Yes' && (
                <FormField
                  control={form.control}
                  name="numberOfLocations"
                  render={({ field }) => (
                    <FormItem className={styles.formItem}>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value?.toString() ?? ''}
                          onChange={(value) => {
                            const num = parseInt(value, 10);
                            field.onChange(isNaN(num) ? undefined : num);
                          }}
                          id="numberOfLocations"
                          type="number"
                          label="How many locations?"
                          placeholder="Enter number of locations"
                          hideIcon
                          labelClassName={styles.label}
                          wrapperClassName={styles.inputWrapper}
                          inputClassName={styles.inputField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>

          {/* Privacy Policy Section */}
          <div className={styles.privacySection}>
            <FormField
              control={form.control}
              name="privacyPolicyAccepted"
              render={({ field }) => (
                <FormItem className={styles.checkboxItem}>
                  <FormControl>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className={styles.checkbox}
                      />
                      <span className={styles.checkboxText}>
                        We need your contact information to communicate about
                        our partnership. You may unsubscribe from these
                        communications at any time. For more information, and
                        our commitment to protecting your privacy, please read
                        and accept our{' '}
                        <a
                          href="https://www.goodneighbor.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.link}
                        >
                          privacy policy
                        </a>{' '}
                        and{' '}
                        <a
                          href="https://www.goodneighbor.com/terms-of-service"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.link}
                        >
                          terms
                        </a>
                        .
                      </span>
                    </label>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={styles.actions}>
            {formErrorMessage && (
              <InfoCard
                severity="error"
                role="alert"
                className={styles.formError}
              >
                {formErrorMessage}
              </InfoCard>
            )}
            <Button
              type="submit"
              color="primary"
              size="lg"
              isLoading={isSendOtpPending}
              isDisabled={isSendOtpPending || isSlugTaken || isCheckingSlug}
              className={styles.submitButton}
            >
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
}
